import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { HC } from "../hr/theme";
import { Btn, Pill } from "../hr/ui";
import { DataTable, Kpi, cmoney, money, num, dt } from "./crmui";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, Gift, ShieldCheck, XCircle } from "lucide-react";

const TABS = ["Overview", "Orders", "Loyalty", "Preferences", "Activity", "Campaigns"];

export default function CustomerProfile() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [p, setP] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [activity, setActivity] = useState([]);
  const [camp, setCamp] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ points: "", reason: "", method: "admin" });

  const load = useCallback(() => {
    api.get(`/crm/customers/${cid}`).then(setP).catch((e) => toast.error(formatApiError(e)));
    api.get(`/crm/customers/${cid}/loyalty`).then(setLoyalty).catch(() => {});
  }, [cid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "Orders") api.get(`/crm/customers/${cid}/orders`).then(setOrders).catch(() => {});
    if (tab === "Activity") api.get(`/crm/customers/${cid}/activity`).then(setActivity).catch(() => {});
    if (tab === "Campaigns") api.get(`/crm/customers/${cid}/campaigns`).then(setCamp).catch(() => {});
  }, [tab, cid]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); setModal(null); setForm({ points: "", reason: "", method: "admin" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!p) return <div className="h-48 rounded-2xl border bg-white animate-pulse" style={{ borderColor: HC.border }} />;
  const c = p.customer;
  const acct = p.loyalty_account || {};

  return (
    <div className="space-y-4" data-testid="customer-profile">
      <button onClick={() => navigate("/crm/customers")} data-testid="profile-back"
        className="text-[12.5px] font-semibold flex items-center gap-1.5" style={{ color: HC.muted }}>
        <ArrowLeft size={14} /> Back to customers
      </button>

      <div className="rounded-2xl bg-white border p-5" style={{ borderColor: HC.border }} data-testid="profile-header">
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-brand font-bold text-xl" style={{ color: HC.text }} data-testid="profile-name">{c.name}</h2>
              <Pill status={c.crm_status} text={c.crm_status} testid="profile-status" />
              <Pill status={c.verification_status === "verified" ? "approved" : "pending"} text={c.verification_status} testid="profile-verification" />
              {acct.wallet_active ? <Pill status="active" text="Wallet active" /> : <Pill status="pending" text="Wallet inactive" testid="profile-wallet-inactive" />}
            </div>
            <div className="text-[12.5px] mt-1.5 flex flex-wrap gap-x-5 gap-y-1" style={{ color: HC.muted }}>
              <span>ID {c.id.slice(0, 8)}</span>
              <span>{c.phone || "no phone"}</span>
              <span>{c.email || "no email"}</span>
              <span>{c.location_name}</span>
              <span>Registered {dt(c.registered_at)} via {c.registration_channel}</span>
              {c.verified_at && <span>Verified {dt(c.verified_at)} ({c.verification_method})</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px]" style={{ color: HC.soft }}>Available points</div>
            <div className="font-brand font-bold text-2xl" style={{ color: HC.gold }} data-testid="profile-points">{num(acct.points_available)}</div>
            <div className="text-[11.5px]" style={{ color: HC.muted }}>{acct.tier_name} tier</div>
            {acct.points_pending > 0 && <div className="text-[11px]" style={{ color: HC.amber }}>{num(acct.points_pending)} pending verification</div>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {c.verification_status !== "verified" && (
            <>
              <Btn onClick={() => setModal("verify")} data-testid="profile-verify"><BadgeCheck size={14} /> Verify customer</Btn>
              <Btn variant="danger" onClick={() => setModal("reject")} data-testid="profile-reject"><XCircle size={14} /> Reject verification</Btn>
            </>
          )}
          <Btn variant="outline" onClick={() => setModal("adjust")} data-testid="profile-adjust"><Gift size={14} /> Adjust points</Btn>
          <Btn variant="outline" onClick={() => setModal("redeem")} data-testid="profile-redeem"><Gift size={14} /> Redeem points</Btn>
          <Btn variant="outline" data-testid="profile-consent"
            onClick={() => act(() => api.post(`/crm/customers/${cid}/consent`, { channel: "email", granted: !c.marketing_consent }),
                                c.marketing_consent ? "Consent revoked" : "Consent granted")}>
            <ShieldCheck size={14} /> {c.marketing_consent ? "Revoke marketing consent" : "Grant marketing consent"}
          </Btn>
        </div>
        {(p.open_duplicates || []).length > 0 && (
          <div className="mt-3 rounded-xl p-3 text-[12.5px]" style={{ background: HC.amberTint, color: HC.amber }} data-testid="profile-duplicate-warning">
            {p.open_duplicates.length} possible duplicate account(s) detected —{" "}
            <button className="underline font-semibold" onClick={() => navigate("/crm/duplicates")}>review and merge</button>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`profile-tab-${t.toLowerCase()}`}
            className="h-9 px-4 rounded-full text-[12.5px] font-semibold border"
            style={tab === t ? { background: HC.burgundy, color: "#fff", borderColor: HC.burgundy } : { background: "#fff", color: HC.text, borderColor: HC.border }}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="space-y-4" data-testid="profile-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testid="ov-orders" label="Total orders" value={num(c.orders)} />
            <Kpi testid="ov-spend" label="Lifetime spend" value={cmoney(c.spend)} />
            <Kpi testid="ov-aov" label="Average order value" value={cmoney(c.avg_order)} />
            <Kpi testid="ov-last" label="Last purchase" value={dt(c.last_purchase)} />
            <Kpi testid="ov-first" label="First purchase" value={dt(c.first_purchase)} />
            <Kpi testid="ov-frequency" label="Purchase frequency"
              value={c.orders && c.registered_days ? `${(c.orders / Math.max(c.registered_days / 30, 1)).toFixed(2)}/mo` : "—"} />
            <Kpi testid="ov-channel" label="Preferred channel" value={c.top_channel || "—"} />
            <Kpi testid="ov-location" label="Preferred location" value={c.location_name} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <DataTable testid="ov-fav-products" columns={[["name", "Favourite products"], ["count", "Qty"]]}
              rows={p.preferences.favourite_products} empty="No purchases yet" />
            <DataTable testid="ov-fav-categories" columns={[["name", "Favourite categories"], ["count", "Qty"]]}
              rows={p.preferences.favourite_categories} empty="No purchases yet" />
            <DataTable testid="ov-segments" columns={[["name", "Segment membership"]]}
              rows={p.segments} empty="Not in any segment" />
          </div>
        </div>
      )}

      {tab === "Orders" && (
        <DataTable testid="profile-orders" rows={orders}
          columns={[["number", "Order"], ["date", "Date"], ["location", "Location"], ["channel", "Channel"],
                    ["items", "Items"], ["total", "Amount"], ["payment_status", "Payment"], ["order_status", "Status"]]}
          render={{ date: (r) => dt(r.date), total: (r) => money(r.total),
                    payment_status: (r) => <Pill status={r.payment_status} text={r.payment_status} />,
                    order_status: (r) => <Pill status={r.order_status} text={r.order_status} /> }}
          onRowClick={() => navigate("/orders")} empty="No orders for this customer" />
      )}

      {tab === "Loyalty" && loyalty && (
        <div className="space-y-4" data-testid="profile-loyalty">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testid="ly-tier" label="Current tier" value={loyalty.account?.tier_name || "—"} />
            <Kpi testid="ly-available" label="Available points" value={num(loyalty.account?.points_available)} tone="gold" />
            <Kpi testid="ly-pending" label="Pending points" value={num(loyalty.account?.points_pending)} tone="amber" />
            <Kpi testid="ly-redeemed" label="Redeemed points" value={num(loyalty.account?.points_redeemed)} />
            <Kpi testid="ly-expired" label="Expired points" value={num(loyalty.account?.points_expired)} />
            <Kpi testid="ly-earned" label="Total earned" value={num(loyalty.account?.lifetime_earned)} />
            <Kpi testid="ly-wallet" label="Wallet status" value={loyalty.account?.wallet_active ? "Active" : "Pending verification"} />
            <Kpi testid="ly-tier-since" label="Tier since" value={dt(loyalty.account?.tier_since)} />
          </div>
          <DataTable testid="profile-loyalty-ledger" rows={loyalty.transactions}
            columns={[["txn_no", "Transaction"], ["created_at", "Date"], ["type", "Type"], ["points", "Points"],
                      ["previous_balance", "Previous"], ["new_balance", "New"], ["source", "Source"],
                      ["order_number", "Order"], ["reason", "Reason"], ["user_name", "By"]]}
            render={{ created_at: (r) => dt(r.created_at), type: (r) => <Pill status={r.type === "redeem" ? "pending" : "approved"} text={r.type} /> }}
            empty="No loyalty transactions yet" />
          <DataTable testid="profile-tier-history" rows={loyalty.tier_history}
            columns={[["at", "When"], ["from_tier", "From"], ["to_tier", "To"], ["direction", "Movement"], ["by", "By"]]}
            render={{ at: (r) => dt(r.at) }} empty="No tier movements yet" />
        </div>
      )}

      {tab === "Preferences" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="profile-preferences">
          <DataTable testid="pref-products" columns={[["name", "Favourite products"], ["count", "Qty"]]} rows={p.preferences.favourite_products} empty="No purchases yet" />
          <DataTable testid="pref-categories" columns={[["name", "Favourite categories"], ["count", "Qty"]]} rows={p.preferences.favourite_categories} empty="No purchases yet" />
          <DataTable testid="pref-combos" columns={[["name", "Frequent combinations"], ["count", "Times"]]} rows={p.preferences.favourite_combinations} empty="No repeat combinations yet" />
          <div className="rounded-2xl bg-white border p-5 space-y-2" style={{ borderColor: HC.border }}>
            <h3 className="font-brand font-bold text-base" style={{ color: HC.text }}>Stored preferences</h3>
            <div className="text-[12.5px]" style={{ color: HC.muted }}>Preferred location: {c.location_name}</div>
            <div className="text-[12.5px]" style={{ color: HC.muted }}>Preferred channel: {c.top_channel || "—"}</div>
            <div className="text-[12.5px]" style={{ color: HC.muted }}>Marketing consent: {c.marketing_consent ? "granted" : "not granted"}</div>
            <DataTable testid="pref-consents" columns={[["at", "When"], ["channel", "Channel"], ["status", "Consent"], ["user_name", "By"]]}
              rows={p.consents} render={{ at: (r) => dt(r.at) }} empty="No consent records" />
          </div>
        </div>
      )}

      {tab === "Activity" && (
        <div className="rounded-2xl bg-white border p-5" style={{ borderColor: HC.border }} data-testid="profile-activity">
          {activity.length === 0 ? (
            <div className="text-[12.5px] py-8 text-center" style={{ color: HC.soft }}>No activity recorded yet</div>
          ) : (
            <ol className="space-y-3">
              {activity.map((a, i) => (
                <li key={a.id || i} className="flex gap-3" data-testid={`activity-${i}`}>
                  <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: HC.gold }} />
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold" style={{ color: HC.text }}>{a.title}</div>
                    <div className="text-[11.5px]" style={{ color: HC.soft }}>
                      {a.event_type.replace(/_/g, " ")} · {dt(a.at)} · {a.source} · {a.user_name}
                      {a.detail ? ` · ${a.detail}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {tab === "Campaigns" && camp && (
        <div className="space-y-4" data-testid="profile-campaigns">
          <DataTable testid="prof-campaigns" rows={camp.campaigns}
            columns={[["campaign_name", "Campaign"], ["channel", "Channel"], ["status", "Membership"], ["reason", "Note"], ["at", "When"]]}
            render={{ at: (r) => dt(r.at), status: (r) => <Pill status={r.status === "skipped" ? "rejected" : "pending"} text={r.status} /> }}
            empty="Not enrolled in any campaign" />
          <DataTable testid="prof-journeys" rows={camp.journeys}
            columns={[["journey_name", "Journey"], ["status", "Status"], ["step_index", "Step"], ["enrolled_at", "Enrolled"]]}
            render={{ enrolled_at: (r) => dt(r.enrolled_at) }} empty="Not enrolled in any journey" />
          <DataTable testid="prof-comms" rows={camp.communications}
            columns={[["at", "When"], ["channel", "Channel"], ["subject", "Subject"], ["status", "Delivery"], ["provider", "Provider"]]}
            render={{ at: (r) => dt(r.at), status: (r) => <Pill status={r.status === "not_configured" ? "pending" : "approved"} text={r.status} /> }}
            empty="No communications yet" />
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-md p-5 space-y-3" style={{ borderColor: HC.border }} data-testid={`modal-${modal}`}>
            <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>
              {modal === "verify" ? "Verify customer" : modal === "reject" ? "Reject verification"
                : modal === "adjust" ? "Manual point adjustment" : "Redeem points"}
            </h3>
            {(modal === "adjust" || modal === "redeem") && (
              <input type="number" placeholder="Points" value={form.points} data-testid="modal-points"
                onChange={(e) => setForm({ ...form, points: e.target.value })}
                className="h-10 px-3 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />
            )}
            <input placeholder={modal === "reject" ? "Reason for rejection *" : "Reason / note"} value={form.reason}
              data-testid="modal-reason" onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="h-10 px-3 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />
            <div className="text-[11.5px]" style={{ color: HC.soft }}>
              Every change writes a loyalty ledger entry and an audit record — balances are never edited directly.
            </div>
            <div className="flex justify-end gap-2">
              <Btn variant="outline" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn data-testid="modal-confirm" onClick={() => {
                if (modal === "verify") act(() => api.post(`/crm/customers/${cid}/verify`, { method: "admin", notes: form.reason }), "Customer verified — wallet active");
                else if (modal === "reject") act(() => api.post(`/crm/customers/${cid}/reject-verification`, { reason: form.reason }), "Verification rejected");
                else if (modal === "adjust") act(() => api.post(`/crm/customers/${cid}/loyalty/adjust`, { points: Number(form.points), reason: form.reason }), "Points adjusted");
                else act(() => api.post(`/crm/customers/${cid}/loyalty/redeem`, { points: Number(form.points), reason: form.reason || "Points redemption" }), "Points redeemed");
              }}>Confirm</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
